import React from "react";
import * as Sentry from "@sentry/react";
import { contrastRatio } from "./PanelErrorBoundary";

/**
 * Issue #647 — the untouched half of #484.
 *
 * #634 wrapped the client profile's panels (the half #484 called higher
 * value), but every one of those boundaries is a descendant of Layout. A
 * throw in Layout, in a page component or its hooks, or on any non-profile
 * route still reached the unguarded React root and unmounted the whole tree
 * — #484's reported production symptom (a caregiver on /client_profile/93
 * losing the entire page to one unguarded `.id`).
 *
 * This is the LAST line, not the first: the panel boundaries stay, and a
 * panel throw still degrades to that panel. This only catches what escapes
 * them all — which is why the fallback is a full page with a reload
 * affordance, not the panel card: when it renders there is no surviving
 * page around it to lean on.
 *
 * Same limitation as the panel boundary, stated rather than implied: a React
 * error boundary observes render/lifecycle/constructor throws in its
 * descendants. It does not observe a rejected async loader or a failure a
 * component catches itself (#621's class, still open).
 *
 * NO ERROR TEXT reaches the screen — a thrown message can carry a client id,
 * and this is the one place a stack trace would reach a caregiver. The same
 * property #634 established for the panel fallback, asserted the same way.
 */

/** The fallback paints its OWN ground. At root failure Layout's `bg-black`
 *  never mounts, so the document background is whatever index.css left —
 *  inheriting it means the copy's contrast is decided by a file that never
 *  heard of this component. Both halves of the pair are exported so the AA
 *  ratio is asserted, not eyeballed. */
export const ROOT_FALLBACK_BG = "#000000";
export const ROOT_FALLBACK_FG = "#E8EAED"; // 17.4:1 on #000, by the exported contrastRatio helper

// Fail at import in development if the pair ever drifts below AA — the test
// asserts it too, but the test only guards the constants it imports.
if (process.env.NODE_ENV !== "production") {
  if (contrastRatio(ROOT_FALLBACK_FG, ROOT_FALLBACK_BG) < 4.5) {
    throw new Error("RootErrorBoundary fallback colours fell below WCAG AA");
  }
}

/** The production reload path, exported so the DEFAULT branch is testable —
 *  a test that only injects `onReload` bypasses the branch users actually
 *  receive, and an inert reload button would ship green (#653 review). */
export const defaultReload = () => window.location.reload();

/** Exported so the test can exercise the REAL callback with a mocked scope —
 *  a fixture-populated tag test passes with the callback deleted (#634). */
export const rootBeforeCapture = (scope) => {
  // "root" vs "panel" is what lets Sentry tell a contained panel failure
  // from a whole-app one. It carries no client data.
  scope.setTag("boundary", "root");
};

const fallbackStyle = {
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "16px",
  padding: "32px",
  textAlign: "center",
  backgroundColor: ROOT_FALLBACK_BG,
  color: ROOT_FALLBACK_FG,
};

const buttonStyle = {
  padding: "10px 24px",
  borderRadius: "8px",
  border: "1px solid #5F6368",
  backgroundColor: "#FFFFFF",
  color: "#000000",
  fontSize: "1rem",
  cursor: "pointer",
};

const RootErrorBoundary = ({ children, onReload }) => (
  <Sentry.ErrorBoundary
    fallback={
      <div role="alert" style={fallbackStyle}>
        <h1 style={{ fontSize: "1.4rem", margin: 0 }}>
          Something went wrong
        </h1>
        <p style={{ maxWidth: "36rem", margin: 0 }}>
          This is a problem with the app, not with any client&apos;s record —
          the information may still be there. Reloading usually fixes it.
        </p>
        <button
          type="button"
          style={buttonStyle}
          onClick={onReload || defaultReload}
        >
          Reload the app
        </button>
      </div>
    }
    beforeCapture={rootBeforeCapture}
  >
    {children}
  </Sentry.ErrorBoundary>
);

export default RootErrorBoundary;
